export default function LogonFactory(deps, options) {
  const { api, dataStore, sso } = deps;
  const { app, pid = null } = options;

  return {
    getProviderId() {
      const user = dataStore.get('user');
      let providerId = null;

      if (app === 'admin' || app === 'utility') {
        const { roles } = user;
        const providerRoles = roles.filter(({ role }) => ['manager', 'user', 'platoon-user', 'c-user', 'c-manager', 'shift::read', 'envision::read', 'envision::write::view'].includes(role));
        const isAdmin = roles.find(({ role }) => ['admin', 'admin-ro'].includes(role));
        providerId = dataStore.get('pid') || null;

        // admin case
        if (isAdmin) {
          if (app !== 'admin') {
            providerId = providerId || 1;
            dataStore.set('pid', providerId);
          }
          return providerId;
        }

        // manager or user case
        if (providerRoles.length) {
          providerId = providerId || providerRoles[0].providerId;
          dataStore.set('pid', providerId);
          return providerId;
        }

        dataStore.delete('pid');
        return null;
      }

      if (app === 'home') {
        providerId = pid;
        dataStore.set('pid', providerId);
        return providerId;
      }

      if (app === 'gravity') {
        providerId = providerId || 1;
        dataStore.set('pid', providerId);
        return providerId;
      }

      return null;
    },
    login(opts = {}) {
      dataStore.set('setRefreshing', new Date());
      const { uid, token: newToken, totp = false, usingPassword = false, usingSaml = false } = opts;
      const tokenTime = new Date().toISOString();
      return api.post('/auth/login', {
        uid, token: newToken, totp, usingPassword, usingSaml,
      })
        .then((data) => {
          const { token, ...user } = data;
          dataStore.set('user', user);
          dataStore.set('uid', uid);
          dataStore.set('token', token);
          dataStore.set('tokenTime', tokenTime);
          const providerId = this.getProviderId();
          if (app !== 'admin' && !providerId) {
            throw new Error('Missing required providerId field');
          }

          return { loggedIn: true, token, uid, usingPassword };
        })
        .catch(({ response }) => {
          const { data = null } = response;
          const status = Number(response.status);
          dataStore.delete('token');
          dataStore.delete('tokenTime');
          dataStore.delete('user');


          if (status === 401) {
            if (data === 'unauthorized') {
              return { loggedIn: false, reason: 'INACTIVE_LINK' };
            }
            return { loggedIn: false, reason: 'INCORRECT_PIN' };
          }

          if (status === 429) {
            return { loggedIn: false, reason: 'LOCKED_OUT' };
          }

          return { loggedIn: false, reason: 'INCORRECT' };
        })
        .finally(() => {
          dataStore.delete('setRefreshing');
        });
    },
    logout() {
      return api.post('/auth/logout')
        .then(() => {
          dataStore.delete('token');
          dataStore.delete('tokenTime');
          dataStore.delete('user');
          dataStore.delete('uid');
          dataStore.delete('pid');
          return window.location.assign('/');
        })
        .catch(() => {
          return false;
        })
        .finally(() => {
          dataStore.delete('setRefreshing');
        });
    },
    passwordLogin(opts) {
      const { user, password } = opts;
      return api.post('/auth/password', { user, password })
        .then((data) => {
          const { uid, token, totp = false } = data;
          if (totp) {
            return { loggedIn: false, reason: 'TOTP', totp, token, uid };
          }
          if ((app === 'admin' || app === 'gravity') && !totp) {
            return { loggedIn: false, reason: 'TOTP_REQUIRED' };
          }
          return this.login({ uid, token, usingPassword: true });
        })
        .then((data) => {
          return data;
        })
        .catch(({ response = {} }) => {
          const { status = null } = response;
          if (status === 429) {
            return { loggedIn: false, reason: 'LOCKED_OUT' };
          }
          return { loggedIn: false, reason: 'INCORRECT' };
        });
    },
    passwordlessLogin(opts) {
      const { user, usePin } = opts;
      let delivery = 'web-pro';

      if (app === 'admin' || app === 'utility') {
        delivery = app === 'admin' ? 'web-admin' : 'web-pro';
        if (usePin) {
          delivery = `${delivery}-pin`;
        }
      }

      if (app === 'gravity') {
        delivery = 'web-gravity';
        if (usePin) {
          delivery = `${delivery}-pin`;
        }
      }

      if (app === 'home') {
        delivery = usePin ? `pin-${pid}` : `web-house-${pid}`;
      }

      return api.post('/auth/passwordless', { user, delivery });
    },
    resetPassword(opts) {
      const { email, delivery } = opts;
      return api.delete(`/auth/password?user=${encodeURIComponent(email)}&delivery=${encodeURIComponent(delivery)}`);
    },
    refresh() {
      let savedToken = dataStore.get('token');
      // if there is no current token, return
      if (!savedToken) {
        return new Promise((res) => {
          res({ authenticated: false, status: 'NO_TOKEN' });
        });
      }
      // figure out if we "should" currently be authenticated
      let tokenTime = dataStore.get('tokenTime'); 
      const authenticated = tokenTime && (Date.now() - Date.parse(tokenTime) < 5*60*1000);
      // skip the refresh if some browser started one within the last 60 seconds
      let refreshing = dataStore.get('setRefreshing');
      if (refreshing && (Date.now() - Date.parse(refreshing) < 60 * 1000)) {
        return new Promise((res) => {
          res({ authenticated, status: 'IN_PROGRESS' });
        });
      }
      // make sure any other process knows that we are refreshing
      refreshing = new Date().toISOString();
      dataStore.set('setRefreshing', refreshing);
      // this is used to clean up afterward
      const cleanUpOnClose = () => {
        // only clear the variable if we are the ones that set it
        const testRefreshing = dataStore.get('setRefreshing');
        if (testRefreshing === refreshing) {
          dataStore.delete('setRefreshing');
        }
      };
      // this will fire if the tab is closed in the middle
      window.addEventListener('beforeunload', cleanUpOnClose);
      tokenTime = new Date().toISOString();
      // make sure we have the latest token
      savedToken = dataStore.get('token'); 
      // execute the refresh
      return api.post('/auth/refresh', { token: savedToken })
        .then((data) => {
          const { token } = data;
          dataStore.set('token', token);
          dataStore.set('tokenTime', tokenTime);
          return { authenticated: true, status: 'COMPLETED' };
        })
        .catch(() => {
          dataStore.delete('token');
          dataStore.delete('tokenTime');
          dataStore.delete('user');
          return { authenticated: false, status: 'REJECTED' };
        })
        .finally(() => {
          // clean up
          cleanUpOnClose();
          // remove the event listener
          window.removeEventListener('beforeunload', cleanUpOnClose);
        });
    },
    loginRedirect() {
      if (app !== 'utility') {
        return; // Redirect logic only relevant to utility app due to use for support SSO
      }
      const directLinkUrl = localStorage.getItem('directLinkUrl') || '/';
      const returnToUrl = localStorage.getItem('returnToUrl') || '';

      const redirectUrl = returnToUrl.slice(returnToUrl.indexOf('?') + 1).split('&').reduce((prev, param) => {
        const pieces = param.split('=');
        if (pieces[0] === 'redirect_url') {
          return pieces[1];
        }
        return prev;
      }, '')
        .split('#')
        .shift();

      localStorage.removeItem('returnToUrl');

      if (redirectUrl) {
        const supportUrl = 'https://support.virtual-peaker.com';
        const ssoPath = redirectUrl.includes(supportUrl) ? redirectUrl : supportUrl;
        return sso.getSsoUrl(ssoPath)
          .then((url) => { window.location.assign(url) });
      }

      return window.location.assign(directLinkUrl);
    },
    getHouseLink(houseLink) {
      return api.get(`/account/link/${houseLink}`);
    },
    linkAccount(opts) {
      return api.post('/account/link', opts);
    },
  };
}
